import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from "react-dom";

const registered = [];

/**
  * Call to register a selector with the component.
  *
  * @param {string}    selector  querySelectorAll compatible selector.
  * @param {WPElement} component Unrendered component.
  */
export function RegisterBlock(selector, component) {
  registered.push({ component, selector });
}

/**
  * Parses the passed node for registered blocks.
  *
  * @param {Object}      props      React props.
  * @param {HTMLElement} props.node HTML DOM element.
  */
export function BlockPortal({ node }) {
  const [render, setRender] = useState(null);

  useEffect(() => {
    const exec = (element) => {
      const portals = [];

      for (const { component: Component, selector } of registered) {
        const nodes = element.querySelectorAll(selector);

        if (nodes) {
          // If we have nodes to render, recurse through the elements moving the div to the children (moves the node into the portal).
          for (const _n of nodes) {
            const props = {};

            for (const att of _n.attributes) {
              let _key = att.name.replace('data-', '');

              if (_key === 'class') _key = 'className';

              props[_key] = att.value;
            }

            _n.removeAttribute('class');
            const newdiv = document.createElement('div');
            _n.after(newdiv);

            const children = [];

            if (_n.innerHTML && _n.innerHTML.trim()) {
              const inners = exec(_n);
              if (inners?.length) {
                children.push(...inners);
              }
            }

            portals.push(
              createPortal(
                <Component element={_n} {...props}>
                  <ChildrenRender element={_n}>
                    {children}
                  </ChildrenRender>
                </Component>,
                newdiv
              )
            );
          }
        } else {
          // If there are no blocks to render, we need to move the html inside portal for nested children.
          const newdiv = document.createElement('div');
          element.after(newdiv);

          portals.push(
            createPortal(
              <ChildrenRender element={element} />,
              newdiv
            )
          );
        }
      }

      return portals;
    };

    if (node.current) {
      setRender(exec(node.current));
    }
  }, [setRender, node]);

  return render;
}

/**
  * Moves any Inner blocks into the children renderer.
  *
  * @param {Object}      props          Component props.
  * @param {HTMLElement} props.element  Element to be moved into the component.
  * @param {WPElement}   props.children Nested children.
  */
function ChildrenRender({ element, children }) {
  const divref = useRef();

  useEffect(() => {
    if (element && divref.current) {
      divref.current.appendChild(element);
    }
  }, [divref, element]);

  return <div ref={divref}>{children}</div>;
}
